
import { Component, EventEmitter, Output, Input, HostListener, ViewChild, signal, inject, OnInit, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PlayerLookupModalComponent } from '../../player-lookup-modal/player-lookup-modal.component';
import { ModulesPlayersApplicationPlayersPlayerResponse } from '../../../api-client/model/modulesPlayersApplicationPlayersPlayerResponse';
import { ModulesTournamentsApplicationTournamentPlayerCategoriesTournamentPlayerCategoryResponse } from '../../../api-client/model/modulesTournamentsApplicationTournamentPlayerCategoriesTournamentPlayerCategoryResponse';
import { TournamentService } from '../../../services/tournament/tournament.service';
import { TourService } from '../../../services/tour/tour.service';
import { RefService } from '../../../services/reference/reference.service';
import { Tour } from '../../../models/tour';
import { Country } from '../../../models/reference/country';
import { State } from '../../../models/reference/state';

export interface PlayerFormData {
    playerId?: string;
    tournamentId?: string;
    tourId: string;
    shortName: string;
    firstName: string;
    middleName: string;
    lastName: string;
    countryId: number;
    countryCode?: string;
    stateId: number;
    stateCode?: string;
    hometown: string;
    lpgaId: string;
    lpgaAccountId: string;
    wwgrId: string;
    categoryId: number;
    categoryName?: string;
    fieldRank: string;
    aRank: string;
    abcType: string;
    r1Wave: string;
    isAmateur: boolean;
    isSponsorInvite: boolean;
    isQualifier: boolean;
    isTitleist: boolean;
    isCmeEligible: boolean;
}

@Component({
    selector: 'app-player-modal',
    standalone: true,
    templateUrl: './player-modal.component.html',
    styleUrls: ['./player-modal.component.scss'],
    imports: [FormsModule, PlayerLookupModalComponent],
})
export class PlayerModalComponent implements OnInit {
    @ViewChild(PlayerLookupModalComponent) playerLookupModal!: PlayerLookupModalComponent;
    @ViewChild('tourDropdown') tourDropdown!: ElementRef<HTMLSelectElement>;

    @Input() existingPlayerIds: string[] = [];
    @Output() playerSaved = new EventEmitter<PlayerFormData>();

    private tournamentService = inject(TournamentService);
    private tourService = inject(TourService);
    private referenceService = inject(RefService);

    isVisible = false;
    isEditMode = false;
    isExistingPlayer = signal<boolean>(false);
    categories = signal<ModulesTournamentsApplicationTournamentPlayerCategoriesTournamentPlayerCategoryResponse[]>([]);
    tours = signal<Tour[]>([]);
    countries = signal<Country[]>([]);
    states = signal<State[]>([]);
    isFormChanged = false;
    private playerFormBaselineSignature = '';
    private hasCapturedFormBaseline = false;

    ngOnInit(): void {
        this.loadCategories();
        this.loadTours();
        this.loadCountries();
        this.loadStates();
    }

    private loadCategories(): void {
        this.tournamentService.getTournamentPlayerCategoriesEndpoint().subscribe({
            next: (categories) => {
                const sortedCategories = categories.sort((a, b) => (a.rank || 0) - (b.rank || 0));
                this.categories.set(sortedCategories);

                if (this.playerForm.categoryId === 0 && sortedCategories.length > 0) {
                    this.playerForm.categoryId = sortedCategories[0].tournamentPlayerCategoryID || 0;
                }
            },
            error: (error) => {
                console.error('Error loading tournament player categories:', error);
            }
        });
    }

    private loadTours(): void {
        this.tourService.getAll().subscribe({
            next: (tours) => {
                const activeTours = tours.filter(tour => tour.isActive !== false);
                const sortedTours = activeTours.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                this.tours.set(sortedTours);
            },
            error: (error) => {
                console.error('Error loading tours:', error);
            }
        });
    }

    private loadCountries(): void {
        this.referenceService.getCountries().subscribe({
            next: (countries) => {
                const sortedCountries = countries.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                this.countries.set(sortedCountries);
                this.onCountryChanged(this.playerForm.countryId);
            },
            error: (error) => {
                console.error('Error loading countries:', error);
            }
        });
    }

    private loadStates(): void {
        this.referenceService.getStates().subscribe({
            next: (states) => {
                const sortedStates = states.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                this.states.set(sortedStates);
                this.ensureStateMatchesCountry();
                if (this.playerForm.stateId) {
                    this.onStateChanged(this.playerForm.stateId);
                }
            },
            error: (error) => {
                console.error('Error loading states:', error);
            }
        });
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (this.isVisible && event.key === 'Escape') {
            this.closeModal();
        }
    }

    playerForm: PlayerFormData = {
        tourId: '',
        shortName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        countryId: 0,
        countryCode: '',
        stateId: 0,
        stateCode: '',
        hometown: '',
        lpgaId: '',
        lpgaAccountId: '',
        wwgrId: '',
        categoryId: 0,
        categoryName: '',
        fieldRank: '',
        aRank: '',
        abcType: '',
        r1Wave: '',
        isAmateur: false,
        isSponsorInvite: false,
        isQualifier: false,
        isTitleist: false,
        isCmeEligible: false
    };

    openModal(player?: PlayerFormData): void {
        this.hasCapturedFormBaseline = false;
        this.isFormChanged = false;
        this.playerFormBaselineSignature = '';
        this.isEditMode = !!player;
        if (player) {
            this.playerForm = { ...player };
            this.playerForm.tourId = this.playerForm.tourId ?? '';
            this.playerForm.countryId = this.playerForm.countryId ?? 0;
            this.playerForm.stateId = this.playerForm.stateId ?? 0;
            this.playerForm.countryCode = this.playerForm.countryCode ?? '';
            this.playerForm.stateCode = this.playerForm.stateCode ?? '';
            this.isExistingPlayer.set(!!player.playerId);
            this.onCountryChanged(this.playerForm.countryId);
            if (this.playerForm.stateId) {
                this.onStateChanged(this.playerForm.stateId);
            }
        } else {
            this.resetForm();
        }
        this.isVisible = true;
        this.captureFormBaseline();

        setTimeout(() => {
            this.tourDropdown?.nativeElement?.focus();
        }, 0);
    }

    closeModal(): void {
        this.isVisible = false;
        this.resetForm();
    }

    savePlayer(): void {
        if (!this.canSave) {
            return;
        }
        this.playerSaved.emit({ ...this.playerForm });
        this.closeModal();
    }

    newPlayer(): void {
        this.resetForm();
        this.captureFormBaseline();
    }

    openPlayerLookup(): void {
        this.playerLookupModal.openModal(this.existingPlayerIds);
    }

    onExistingPlayerSelected(player: ModulesPlayersApplicationPlayersPlayerResponse): void {
        const defaultCategoryId = this.categories().length > 0
            ? (this.categories()[0].tournamentPlayerCategoryID || 0)
            : 0;

        this.playerForm.playerId = player.playerId;
        this.playerForm.tourId = player.tourId || '';
        this.playerForm.shortName = player.shortName || '';
        this.playerForm.firstName = player.firstName || '';
        this.playerForm.middleName = player.middleName || '';
        this.playerForm.lastName = player.lastName || '';
        this.playerForm.countryId = player.countryId || 0;
        this.playerForm.countryCode = player.countryCode || '';
        this.playerForm.stateId = player.stateId || 0;
        this.playerForm.stateCode = player.stateCode || '';
        this.playerForm.hometown = player.hometown || '';
        this.playerForm.lpgaId = player.lpgaId?.toString() || '';
        this.playerForm.lpgaAccountId = player.lpgaAccountId?.toString() || '';
        this.playerForm.wwgrId = player.wwgrId?.toString() || '';
        this.playerForm.categoryId = defaultCategoryId;
        this.playerForm.categoryName = '';
        this.playerForm.fieldRank = '';
        this.playerForm.aRank = '';
        this.playerForm.abcType = '';
        this.playerForm.r1Wave = '';

        this.isExistingPlayer.set(true);
        this.onCountryChanged(this.playerForm.countryId);
        if (this.playerForm.stateId) {
            this.onStateChanged(this.playerForm.stateId);
        }
        this.evaluateFormChanges();
    }

    private resetForm(): void {
        const defaultCategoryId = this.categories().length > 0
            ? (this.categories()[0].tournamentPlayerCategoryID || 0)
            : 0;

        this.playerForm = {
            tourId: '',
            shortName: '',
            firstName: '',
            middleName: '',
            lastName: '',
            countryId: 0,
            countryCode: '',
            stateId: 0,
            stateCode: '',
            hometown: '',
            lpgaId: '',
            lpgaAccountId: '',
            wwgrId: '',
            categoryId: defaultCategoryId,
            categoryName: '',
            fieldRank: '',
            aRank: '',
            abcType: '',
            r1Wave: '',
            isAmateur: false,
            isSponsorInvite: false,
            isQualifier: false,
            isTitleist: false,
            isCmeEligible: false
        };
        this.isExistingPlayer.set(false);
        this.isFormChanged = false;
        this.hasCapturedFormBaseline = false;
        this.playerFormBaselineSignature = '';
    }

    onTourChanged(tourId: string | null): void {
        this.playerForm.tourId = tourId ?? '';
        this.evaluateFormChanges();
    }

    onCountryChanged(countryId: number | null): void {
        this.playerForm.countryId = countryId ?? 0;
        if (!this.playerForm.countryId) {
            this.playerForm.countryCode = '';
        } else {
            const countries = this.countries();
            if (!countries.length) {
                this.playerForm.countryCode = this.playerForm.countryCode || '';
            } else {
                const selectedCountry = countries.find(country => country.countryId === this.playerForm.countryId);
                this.playerForm.countryCode = selectedCountry?.code || '';
            }
        }
        this.ensureStateMatchesCountry();
        this.evaluateFormChanges();
    }

    onStateChanged(stateId: number | null): void {
        this.playerForm.stateId = stateId ?? 0;
        if (!this.playerForm.stateId) {
            this.playerForm.stateCode = '';
            this.evaluateFormChanges();
            return;
        }

        const states = this.states();
        if (!states.length) {
            return;
        }

        const selectedState = states.find(state => state.stateId === this.playerForm.stateId);
        this.playerForm.stateCode = selectedState?.code || '';
        this.evaluateFormChanges();
    }

    filteredStates(): State[] {
        const states = this.states();
        if (!this.playerForm.countryId) {
            return [];
        }
        return states.filter(state => state.countryId === this.playerForm.countryId);
    }

    private ensureStateMatchesCountry(): void {
        if (!this.playerForm.countryId) {
            this.playerForm.stateId = 0;
            this.playerForm.stateCode = '';
            return;
        }

        const states = this.states();
        if (!states.length) {
            return;
        }

        const matchingStates = states.filter(state => state.countryId === this.playerForm.countryId);
        const hasStateInCountry = matchingStates.some(state => state.stateId === this.playerForm.stateId);
        if (!hasStateInCountry) {
            this.playerForm.stateId = 0;
            this.playerForm.stateCode = '';
        }
    }

    onFormValueChanged(): void {
        this.evaluateFormChanges();
    }

    get canSave(): boolean {
        return this.isFormChanged;
    }

    private captureFormBaseline(): void {
        this.playerFormBaselineSignature = this.snapshotSignature(this.playerForm);
        this.isFormChanged = false;
        this.hasCapturedFormBaseline = true;
    }

    private evaluateFormChanges(): void {
        if (!this.hasCapturedFormBaseline) {
            return;
        }
        this.isFormChanged = this.snapshotSignature(this.playerForm) !== this.playerFormBaselineSignature;
    }

    private snapshotSignature(data: PlayerFormData): string {
        return JSON.stringify(this.buildFormSnapshot(data));
    }

    private buildFormSnapshot(data: PlayerFormData): Record<string, unknown> {
        return {
            playerId: data.playerId ?? '',
            tournamentId: data.tournamentId ?? '',
            tourId: this.normalizeFormString(data.tourId),
            shortName: this.normalizeFormString(data.shortName),
            firstName: this.normalizeFormString(data.firstName),
            middleName: this.normalizeFormString(data.middleName),
            lastName: this.normalizeFormString(data.lastName),
            countryId: this.normalizeNumber(data.countryId),
            countryCode: this.normalizeFormString(data.countryCode),
            stateId: this.normalizeNumber(data.stateId),
            stateCode: this.normalizeFormString(data.stateCode),
            hometown: this.normalizeFormString(data.hometown),
            lpgaId: this.normalizeFormString(data.lpgaId),
            lpgaAccountId: this.normalizeFormString(data.lpgaAccountId),
            wwgrId: this.normalizeFormString(data.wwgrId),
            categoryId: this.normalizeNumber(data.categoryId),
            categoryName: this.normalizeFormString(data.categoryName),
            fieldRank: this.normalizeFormString(data.fieldRank),
            aRank: this.normalizeFormString(data.aRank),
            abcType: this.normalizeFormString(data.abcType),
            r1Wave: this.normalizeFormString(data.r1Wave),
            isAmateur: !!data.isAmateur,
            isSponsorInvite: !!data.isSponsorInvite,
            isQualifier: !!data.isQualifier,
            isTitleist: !!data.isTitleist,
            isCmeEligible: !!data.isCmeEligible
        };
    }

    private normalizeFormString(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private normalizeNumber(value: unknown): number {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
    }
}
